import React, { useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import classNames from 'classnames';
import { useDashboardState } from "components/DashboardContext";
import { useOnClickOutside } from 'utils/helpers';
import './SidebarButtonDropdown.scss';
import useDropdownPosition from 'hooks/useDropdownPosition';
import { getBounding } from "utils/elementExt";

import { ReactComponent as ArrowIcon } from "static/icons/arrow.svg";
import { ReactComponent as Trash } from "static/icons/trash.svg";
import { ReactComponent as PlusCircle } from "static/icons/plus-circle.svg";

const SidebarButtonDropdown = ({
  label,
  buttonTitle,
  isDropdownBlocked,
  children,
  dropdownPosition,
  isMaxSmallHeight,
  isFullHeight,
  withAbsolutePosition,
  isItemsEmpty,
  onCloseDropdown,
  dropDownCloseStatus,
  optionsType,
  onClearAll,
  isOpenInitialDropdown,
  handleAddBtnClick,
  withAddBtn = false,
  withClearBtn = true,
}) => {
  const ref = useRef();
  const dropdownRef = useRef();
  const dropdownButtonRef = useRef();

  const {
    checkSidebarDropdownStatus,
    setCheckSidebarDropdownStatus,
  } = useDashboardState();

  const {
    dropdownIsOpen,
    dropdownVerticalPosition,
    position,
    top,
    bottom,
    showdropdown,
    setDropdownIsOpen,
  } = useDropdownPosition(isOpenInitialDropdown, dropdownButtonRef);

  const [marginOffset, setMarginOffset] = useState(0);

  useEffect(() => {
    if (dropDownCloseStatus) {
      setDropdownIsOpen(dropDownCloseStatus);
    }
  }, [dropDownCloseStatus]);

  useOnClickOutside(ref, () => {
    setDropdownIsOpen(false);

    if (dropdownIsOpen) {
      setMarginOffset(0);
    }

    onCloseDropdown?.(false);
  });

  const buttonDropdownStyles = classNames({
    'sidebar-button-dropdown': true,
    'sidebar-button-dropdown--left': dropdownPosition === 'left',
    'sidebar-button-dropdown--right': dropdownPosition === 'right',
    'sidebar-button-dropdown--max-small-height': isMaxSmallHeight,
    'sidebar-button-dropdown--full-height': isFullHeight,
    'sidebar-button-dropdown--empty': isItemsEmpty,
    'sidebar-button-dropdown--top': dropdownVerticalPosition === 'top',
  });

  const handleClearAll = () => {
    onClearAll(optionsType);
  };

  useEffect(() => {
    if (checkSidebarDropdownStatus && dropdownIsOpen) {
      const height = dropdownRef?.current?.clientHeight;
      const offsetTop = getBounding(dropdownRef?.current);
      const windowInnerHeight = window.innerHeight;

      if (marginOffset) {
        if (height + offsetTop.top + 20 + marginOffset > windowInnerHeight) {
          setMarginOffset(height + offsetTop.top + marginOffset - windowInnerHeight);
        } else {
          setMarginOffset(0);
        }
      } else {
        if (height + offsetTop.top + 20 > windowInnerHeight) {
          setMarginOffset(height + offsetTop.top - windowInnerHeight);
        } else {
          setMarginOffset(0);
        }
      }

      setCheckSidebarDropdownStatus(false);
    }
  }, [checkSidebarDropdownStatus, dropdownIsOpen, marginOffset, setCheckSidebarDropdownStatus]);

  const absolutePositionStyles = withAbsolutePosition
    ? {
      top: top,
      bottom: bottom,
      insetInlineStart: position?.left,
      maxWidth: position.width,
    }
    : {
      top: top,
      bottom: bottom,
    };

  return (
    <div
      ref={ref}
      className={
        classNames({
          'sidebar-button-dropdown__container': true,
          'sidebar-button-dropdown__container--center': dropdownPosition === 'center',
        })
      }
    >
      <div className="sidebar-button-dropdown__header">
        <p className="sidebar-button-dropdown__header_label">
          {label}
        </p>

        <div className='sidebar-button-dropdown__header__controls'>
          {withAddBtn && (
            <p
              className="sidebar-button-dropdown__header__controls__control"
              onClick={handleAddBtnClick}
            >
              <PlusCircle />
            </p>
          )}

          {withClearBtn && (
            <p
              className="sidebar-button-dropdown__header__controls__control"
              onClick={handleClearAll}
            >
              <Trash />
            </p>
          )}
        </div>
      </div>

      <div
        ref={dropdownButtonRef}
        className={classNames("sidebar-button-dropdown__wrapper", {
          "sidebar-button-dropdown__wrapper--active": dropdownIsOpen,
        })}
        onClick={showdropdown}
      >
        <p className={classNames("sidebar-button-dropdown__wrapper_text", {
          "sidebar-button-dropdown__wrapper_text--active": dropdownIsOpen,
        })}>
          {buttonTitle}
        </p>

        <ArrowIcon
          className={classNames("sidebar-button-dropdown__wrapper_icon", {
            "sidebar-button-dropdown__wrapper_icon--active": dropdownIsOpen,
          })}
        />
      </div>

      {dropdownIsOpen && !isDropdownBlocked &&
        <div
          ref={dropdownRef}
          className={buttonDropdownStyles}
          style={absolutePositionStyles}
        >
          {children}
        </div>
      }
    </div>
  );
};

export default SidebarButtonDropdown;
